// useCallHealth (LUM-82)
//
// Mid-call banners for a live interview — tab hidden, connection
// unstable, mic flat while the respondent is expected to speak — and
// a record of what was observed during the call, read at call end to
// choose a cause-specific failure message. Observations reset when the
// next call goes live and survive the end of the call they describe.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CALL_BANNER_COPY,
  type CallHealthObservations,
  NO_CALL_HEALTH_ISSUES,
} from '@/features/interview/call-health';

export interface UseCallHealthOptions {
  callActive: boolean;
  /** The realtime service is inside its resume window. */
  reconnecting: boolean;
  /** Flat mic while the interviewer awaits the respondent. */
  micSilent: boolean;
}

export interface CallHealthBanners {
  tabHidden: boolean;
  connectionUnstable: boolean;
  micSilent: boolean;
}

export interface UseCallHealthReturn {
  banners: CallHealthBanners;
  /** What the current (or just-ended) call ran into. */
  getObservations: () => CallHealthObservations;
}

const HIDDEN_TAB_TITLE = `● ${CALL_BANNER_COPY.tabHidden}`;

export function useCallHealth({
  callActive,
  reconnecting,
  micSilent,
}: UseCallHealthOptions): UseCallHealthReturn {
  const [hidden, setHidden] = useState(
    () => document.visibilityState === 'hidden',
  );
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const observationsRef = useRef<CallHealthObservations>(NO_CALL_HEALTH_ISSUES);
  const wasActiveRef = useRef(false);

  useEffect(() => {
    const onVisibility = () => setHidden(document.visibilityState === 'hidden');
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (callActive && !wasActiveRef.current) {
      observationsRef.current = NO_CALL_HEALTH_ISSUES;
    }
    wasActiveRef.current = callActive;
    if (!callActive) return;
    const seen = observationsRef.current;
    observationsRef.current = {
      wentOffline: seen.wentOffline || offline,
      tabHidden: seen.tabHidden || hidden,
      connectionDropped: seen.connectionDropped || reconnecting,
      micNoAudio: seen.micNoAudio || micSilent,
    };
  }, [callActive, hidden, offline, reconnecting, micSilent]);

  const tabHidden = callActive && hidden;

  // The banner cannot be seen while the tab is hidden; the tab title
  // can. Mirror the message there and restore the title on return.
  useEffect(() => {
    if (!tabHidden) return;
    const previousTitle = document.title;
    document.title = HIDDEN_TAB_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, [tabHidden]);

  const getObservations = useCallback(() => observationsRef.current, []);

  return {
    banners: {
      tabHidden,
      connectionUnstable: callActive && (offline || reconnecting),
      micSilent: callActive && micSilent,
    },
    getObservations,
  };
}
